import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "../lib/api";
import { dateOnly, money } from "../lib/format";
import i18n from "../lib/i18n";

type InvoiceStatus = "draft" | "sent" | "paid" | "void";

interface InvoiceRow {
  id: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  clientName: string;
  currency: string;
  issueDate: string;
  dueDate: string | null;
  total: string;
}

const STATUSES: InvoiceStatus[] = ["draft", "sent", "paid", "void"];

const STATUS_VARIANT: Record<InvoiceStatus, "secondary" | "info" | "success" | "destructive"> = {
  draft: "secondary",
  sent: "info",
  paid: "success",
  void: "destructive",
};

export default function Invoices() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const [, setLocation] = useLocation();

  const [term, setTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | "all">("all");

  const params = new URLSearchParams();
  if (term.trim()) params.set("q", term.trim());
  if (statusFilter !== "all") params.set("status", statusFilter);

  const invoicesQ = useQuery({
    queryKey: ["invoices", term, statusFilter],
    queryFn: () => api<InvoiceRow[]>(`/api/v1/invoices?${params.toString()}`),
  });
  const rows = invoicesQ.data ?? [];

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>{t("invoices:pageTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("invoices:pageSubtitle")}</p>
        </div>
        <Button asChild>
          <Link href="/invoices/new">
            <Plus className="h-4 w-4" aria-hidden />
            {t("invoices:newInvoice")}
          </Link>
        </Button>
      </div>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1 min-w-[12rem]">
          <Label htmlFor="invoices-search" className="sr-only">
            {t("invoices:table.client")}
          </Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id="invoices-search"
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t("invoices:table.client")}
              className="pl-9"
            />
          </div>
        </div>
        <div className="w-full sm:w-44">
          <Label htmlFor="invoices-status" className="sr-only">
            {t("invoices:table.status")}
          </Label>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as InvoiceStatus | "all")}
          >
            <SelectTrigger id="invoices-status" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("sales:filter.allStatuses")}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`invoices:status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {invoicesQ.isLoading && <p className="text-muted-foreground">{t("common:loading")}</p>}

      {!invoicesQ.isLoading && rows.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          {t("invoices:empty")}
        </p>
      )}

      {rows.length > 0 && (
        <Card>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead>{t("invoices:table.number")}</TableHead>
                <TableHead>{t("invoices:table.client")}</TableHead>
                <TableHead className="w-24">{t("invoices:table.status")}</TableHead>
                <TableHead className="hidden text-right sm:table-cell sm:w-32">
                  {t("invoices:table.dueDate")}
                </TableHead>
                <TableHead className="w-28 text-right">{t("invoices:table.total")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer"
                  onClick={() => setLocation(`/invoices/${inv.id}`)}
                >
                  <TableCell>
                    <span className="block truncate font-medium">{inv.invoiceNumber}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {dateOnly(inv.issueDate, locale)}
                    </span>
                  </TableCell>
                  <TableCell className="truncate">{inv.clientName}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[inv.status]}>
                      {t(`invoices:status.${inv.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden text-right text-sm text-muted-foreground sm:table-cell">
                    {inv.dueDate ? dateOnly(inv.dueDate, locale) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {money(inv.total, inv.currency, locale)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
